// SnippetHints - Unified autocomplete popup for both document mode and math mode
// Shows trigger, name, and optional KaTeX preview

import { useEffect, useRef, useState } from "react";

export interface HintItem {
    trigger: string;
    name: string;
    matchedOn: string;
    /** Optional KaTeX-rendered HTML for math previews */
    previewHtml?: string;
}

interface DocHintsProps {
    suggestions: HintItem[];
    selectedIndex: number;
    onSelect: (index: number) => void;
    onHoverIndex?: (index: number) => void;
    visible: boolean;
    position: { top: number; left: number } | null;
}

export function DocHints({
    suggestions,
    selectedIndex,
    onSelect,
    onHoverIndex,
    visible,
    position,
}: DocHintsProps) {
    const listRef = useRef<HTMLDivElement>(null);
    const [adjustedPos, setAdjustedPos] = useState<{ top: number; left: number } | null>(null);

    // Clamp position so popup stays within viewport
    useEffect(() => {
        if (!position || !visible || !listRef.current) {
            setAdjustedPos(position);
            return;
        }
        const rect = listRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let { top, left } = position;

        // If popup would overflow bottom, show above the cursor instead
        if (top + rect.height > vh - 8) {
            top = position.top - rect.height - 28;
        }
        // Clamp horizontal
        if (left + rect.width > vw - 8) {
            left = vw - rect.width - 8;
        }
        if (left < 8) left = 8;
        if (top < 8) top = 8;

        setAdjustedPos({ top, left });
    }, [position, visible, suggestions]);

    useEffect(() => {
        if (listRef.current && visible) {
            const selected = listRef.current.querySelector(".doc-hint-item--selected");
            if (selected) {
                selected.scrollIntoView({ block: "nearest" });
            }
        }
    }, [selectedIndex, visible]);

    if (!visible || suggestions.length === 0 || !position) {
        return null;
    }

    const pos = adjustedPos || position;

    return (
        <div
            className="doc-hints"
            ref={listRef}
            style={{ top: pos.top, left: pos.left }}
        >
            <div className="doc-hints-header">
                <span className="doc-hints-title">Snippets</span>
                <span className="doc-hints-help">↑↓ navigate · Enter select · Esc close</span>
            </div>
            <div className="doc-hints-list">
                {suggestions.map((s, index) => (
                    <div
                        key={`${s.trigger}-${index}`}
                        className={`doc-hint-item ${index === selectedIndex ? "doc-hint-item--selected" : ""}`}
                        onClick={() => onSelect(index)}
                        onMouseEnter={() => onHoverIndex?.(index)}
                    >
                        <code className="doc-hint-code">{s.trigger}</code>
                        <span className="doc-hint-name">{s.name}</span>
                        {s.previewHtml ? (
                            <span
                                className="doc-hint-preview"
                                dangerouslySetInnerHTML={{ __html: s.previewHtml }}
                            />
                        ) : (
                            <span className="doc-hint-matched">({s.matchedOn})</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
